/**
 * Toast Notification Component
 * Auto-dismissing notifications with icons for different types
 * Supports stacking multiple toasts
 */

import React, { useEffect } from 'react';
import { Toast as ToastType } from '@/types';
import { logger } from '@/utils/logger';

// ============================================================================
// TYPES
// ============================================================================

interface ToastItemProps {
  toast: ToastType;
  onDismiss: (id: string) => void;
}

interface ToastContainerProps {
  toasts: ToastType[];
  onDismiss: (id: string) => void;
}

// ============================================================================
// ICON COMPONENTS
// ============================================================================

/**
 * Success icon component
 */
const SuccessIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={`h-5 w-5 ${className}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

/**
 * Error icon component
 */
const ErrorIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={`h-5 w-5 ${className}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

/**
 * Warning icon component
 */
const WarningIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={`h-5 w-5 ${className}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 9v2m0 4v2m0 0v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

/**
 * Info icon component
 */
const InfoIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={`h-5 w-5 ${className}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

// ============================================================================
// TOAST ITEM COMPONENT
// ============================================================================

/**
 * Individual toast notification
 * Displays icon, message, and dismiss button
 * Auto-dismisses after configured duration
 *
 * @component
 */
const ToastItem: React.FC<ToastItemProps> = ({ toast, onDismiss }) => {
  useEffect(() => {
    logger.debug(`Toast rendered`, {
      id: toast.id,
      type: toast.type,
      message: toast.message,
      duration: toast.duration,
    });
  }, [toast]);

  // Auto-dismiss timer
  useEffect(() => {
    const duration = toast.duration || 3000;

    // TODO: Add configurable duration per toast type
    const timer = setTimeout(() => {
      logger.debug(`Toast auto-dismissing`, { id: toast.id });
      onDismiss(toast.id);
    }, duration);

    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onDismiss]);

  /**
   * Get icon component based on toast type
   */
  const getIcon = (): React.ReactNode => {
    const iconProps = { className: '' };
    switch (toast.type) {
      case 'success':
        return <SuccessIcon {...iconProps} />;
      case 'error':
        return <ErrorIcon {...iconProps} />;
      case 'warning':
        return <WarningIcon {...iconProps} />;
      case 'info':
        return <InfoIcon {...iconProps} />;
      default:
        return null;
    }
  };

  /**
   * Get style classes based on toast type
   */
  const getTypeStyles = (): {
    bg: string;
    border: string;
    text: string;
    icon: string;
  } => {
    switch (toast.type) {
      case 'success':
        return {
          bg: 'bg-emerald-900 bg-opacity-80',
          border: 'border-emerald-500',
          text: 'text-emerald-100',
          icon: 'text-emerald-400',
        };
      case 'error':
        return {
          bg: 'bg-red-900 bg-opacity-80',
          border: 'border-red-500',
          text: 'text-red-100',
          icon: 'text-red-400',
        };
      case 'warning':
        return {
          bg: 'bg-yellow-900 bg-opacity-80',
          border: 'border-yellow-500',
          text: 'text-yellow-100',
          icon: 'text-yellow-400',
        };
      case 'info':
        return {
          bg: 'bg-blue-900 bg-opacity-80',
          border: 'border-blue-500',
          text: 'text-blue-100',
          icon: 'text-blue-400',
        };
      default:
        return {
          bg: 'bg-walrus-charcoal',
          border: 'border-walrus-gray',
          text: 'text-walrus-white',
          icon: 'text-walrus-silver',
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <div
      className={`animate-slide-in-up flex items-start gap-3 rounded-lg border ${styles.border} ${styles.bg} p-4 backdrop-blur-sm transition-all duration-300`}
      role="status"
      aria-live="polite"
      aria-label={`${toast.type}: ${toast.message}`}
    >
      {/* Icon */}
      <div className={`flex-shrink-0 pt-0.5 ${styles.icon}`}>{getIcon()}</div>

      {/* Message */}
      <div className={`flex-1 ${styles.text}`}>{toast.message}</div>

      {/* Dismiss button */}
      <button
        onClick={() => {
          logger.debug(`Toast dismissed manually`, { id: toast.id });
          onDismiss(toast.id);
        }}
        className={`flex-shrink-0 rounded-lg p-1 transition-colors hover:bg-walrus-gray focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-walrus-black ${styles.text}`}
        aria-label="Dismiss notification"
        type="button"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
};

ToastItem.displayName = 'ToastItem';

// ============================================================================
// TOAST CONTAINER COMPONENT
// ============================================================================

/**
 * Toast container component
 * Displays multiple toasts in a stacked layout
 * Typically placed at bottom-right of the screen
 *
 * @component
 * @example
 * <ToastContainer toasts={toasts} onDismiss={removeToast} />
 */
export const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  onDismiss,
}) => {
  useEffect(() => {
    logger.debug(`ToastContainer updated`, {
      count: toasts.length,
      toasts: toasts.map((t) => ({ id: t.id, type: t.type })),
    });
  }, [toasts]);

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-3"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      aria-atomic="true"
    >
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  );
};

ToastContainer.displayName = 'ToastContainer';

export default ToastContainer;
